import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Form, InputGroup, Spinner, Badge, ListGroup, Button } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import { toast } from 'react-toastify'

interface Article {
  id: string
  title: string
  excerpt: string
  slug: string
  views: number
  helpful: number
  notHelpful: number
  publishedAt: string
  category: {
    id: string
    name: string
    slug: string
    icon?: string
  } | null
  author: {
    id: string
    name: string
  }
}

interface Category {
  id: string
  name: string
  slug: string
  description: string
  icon?: string
  _count: {
    articles: number
  }
}

export const ArticlesPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [articles, setArticles] = useState<Article[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '')

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchArticles()
  }, [searchParams])

  const fetchCategories = async () => {
    try {
      const response = await api.get('/articles/categories/list')
      setCategories(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const fetchArticles = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      
      const search = searchParams.get('search')
      const category = searchParams.get('category')
      
      if (search) params.append('search', search)
      if (category) params.append('categoryId', category)

      const response = await api.get(`/articles?${params.toString()}`)
      setArticles(response.data.data || [])
    } catch (error) {
      toast.error('Failed to load articles')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (selectedCategory) params.set('category', selectedCategory)
    setSearchParams(params)
  }

  const handleCategoryFilter = (categoryId: string) => {
    setSelectedCategory(categoryId)
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (categoryId) params.set('category', categoryId)
    setSearchParams(params)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="mb-2">
          <i className="fas fa-book me-2 text-primary"></i>
          Knowledge Base
        </h1>
        <p className="text-muted">Browse articles and find answers to your questions</p>
      </div>

      {/* Search */}
      <Row className="mb-4">
        <Col lg={8} xl={6} className="mx-auto">
          <Form onSubmit={handleSearch}>
            <InputGroup size="lg">
              <Form.Control
                type="text"
                placeholder="Search for articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button type="submit" variant="primary">
                <i className="fas fa-search"></i>
              </Button>
            </InputGroup>
          </Form>
        </Col>
      </Row>

      <Row>
        {/* Categories Sidebar */}
        <Col lg={3}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-folder me-2 text-primary"></i>
                Categories
              </h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item
                action
                active={!selectedCategory}
                onClick={() => handleCategoryFilter('')}
                className="d-flex justify-content-between align-items-center"
              >
                <span>All Articles</span>
                <Badge bg="secondary" pill>
                  {categories.reduce((sum, cat) => sum + cat._count.articles, 0)}
                </Badge>
              </ListGroup.Item>
              {categories.map((category) => (
                <ListGroup.Item
                  key={category.id}
                  action
                  active={selectedCategory === category.id}
                  onClick={() => handleCategoryFilter(category.id)}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span>
                    {category.icon && <i className={`${category.icon} me-2`}></i>}
                    {category.name}
                  </span>
                  <Badge bg="secondary" pill>
                    {category._count.articles}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>

          {/* Help Card */}
          <Card className="border-0 bg-light">
            <Card.Body>
              <h6 className="mb-2">
                <i className="fas fa-question-circle me-2 text-info"></i>
                Need more help?
              </h6>
              <p className="small text-muted mb-3">
                Can't find what you're looking for? Submit a ticket and our team will assist you.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/tickets/new')}
                className="w-100"
              >
                <i className="fas fa-ticket-alt me-2"></i>
                Submit a Ticket
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Articles List */}
        <Col lg={9}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <h4 className="text-muted">No articles found</h4>
                <p className="text-muted">
                  {searchTerm || selectedCategory
                    ? 'Try adjusting your filters or search term'
                    : 'No articles available at this time'}
                </p>
              </Card.Body>
            </Card>
          ) : (
            <div className="d-grid gap-3">
              {articles.map((article) => (
                <Card
                  key={article.id}
                  className="border-0 shadow-sm cursor-pointer"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/articles/${article.slug}`)}
                >
                  <Card.Body>
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="flex-grow-1">
                        <h5 className="mb-2">
                          {article.title}
                          {article.category && (
                            <Badge bg="primary" className="ms-2">
                              {article.category.icon && (
                                <i className={`${article.category.icon} me-1`}></i>
                              )}
                              {article.category.name}
                            </Badge>
                          )}
                        </h5>
                        {article.excerpt && (
                          <p className="text-muted mb-2">{article.excerpt}</p>
                        )}
                        <div className="d-flex align-items-center gap-3 text-muted small">
                          <span>
                            <i className="fas fa-user me-1"></i>
                            {article.author.name}
                          </span>
                          <span>
                            <i className="fas fa-eye me-1"></i>
                            {article.views} views
                          </span>
                          <span>
                            <i className="fas fa-thumbs-up me-1"></i>
                            {article.helpful}
                          </span>
                          <span>
                            <i className="fas fa-calendar me-1"></i>
                            {formatDate(article.publishedAt)}
                          </span>
                        </div>
                      </div>
                      <div className="ms-3">
                        <i className="fas fa-chevron-right text-muted"></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </Container>
  )
}







